<template>
  <div class="flex flex-col gap-6">
    <!-- Budget (simple input €, optional) -->
    <div>
      <b-label class="mb-2 block">{{ $t('publish_mission.step3.budget_label') }}</b-label>
      <p class="text-gray-400 text-sm mb-3">{{ $t('publish_mission.step3.budget_hint') }}</p>
      <div class="relative max-w-xs">
        <b-text-field
          v-model="budget"
          name="budget"
          type="number"
          :placeholder="$t('publish_mission.step3.budget_placeholder')"
          min="0"
          step="100"
          pattern="[0-9]*"
          inputmode="numeric"
          class="pr-10"
          data-e2e="step3-budget"
        />
        <span class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
      </div>
    </div>

    <!-- Work Location (chips) - TEMPORARILY HIDDEN: will be re-enabled later
    <div>
      <b-label class="mb-2 block">{{ $t('publish_mission.step3.work_location_label') }}</b-label>
      <RadioGroup v-model="workLocation" data-e2e="step3-work-location-group">
        <div class="flex flex-wrap gap-2">
          <RadioGroupOption
            v-for="location in workLocationOptions"
            :key="location.value"
            v-slot="{ checked }"
            :value="location.value"
            :data-e2e="`step3-location-${location.value}`"
          >
            <BRadioChip :checked="checked">
              {{ location.label }}
            </BRadioChip>
          </RadioGroupOption>
        </div>
      </RadioGroup>
    </div>
    -->

    <!-- Duration (input + Weeks/Months toggle) - TEMPORARILY HIDDEN: will be re-enabled later
    <div>
      <b-label class="mb-2 block">{{ $t('publish_mission.step3.duration_label') }}</b-label>
      <div class="flex items-center gap-4 max-w-md">
        <b-text-field
          v-model="durationValue"
          name="durationValue"
          type="number"
          :placeholder="$t('publish_mission.step3.duration_placeholder')"
          min="1"
          inputmode="numeric"
          class="w-24"
          data-e2e="step3-duration-value"
        />
        <div class="flex items-center gap-2 bg-gray-800 rounded-lg p-1">
          <button
            type="button"
            @click="durationUnit = 'weeks'"
            class="px-4 py-2 text-sm rounded-md transition-colors"
            :class="[durationUnit === 'weeks' ? 'bg-white text-black font-semibold' : 'text-gray-400']"
            data-e2e="step3-duration-weeks"
          >
            {{ $t('publish_mission.step3.weeks') }}
          </button>
          <button
            type="button"
            @click="durationUnit = 'months'"
            class="px-4 py-2 text-sm rounded-md transition-colors"
            :class="[durationUnit === 'months' ? 'bg-white text-black font-semibold' : 'text-gray-400']"
            data-e2e="step3-duration-months"
          >
            {{ $t('publish_mission.step3.months') }}
          </button>
        </div>
      </div>
    </div>
    -->

    <!-- Required languages (chips) -->
    <div>
      <b-label class="mb-2 block">{{ $t('publish_mission.step3.languages') }}</b-label>
      <b-checkbox-group v-model="languages">
        <div class="flex flex-wrap gap-2">
          <b-checkbox-chip
            v-for="code in SPOKEN_LANGUAGES"
            :key="code"
            :value="code"
            data-e2e="step3-language-chip"
          >
            {{ $t('languages.' + code) }}
          </b-checkbox-chip>
        </div>
      </b-checkbox-group>
    </div>

    <!-- Visibility Toggle (Private/Public) -->
    <div>
      <b-label class="mb-2 block">{{ $t('publish_mission.step3.visibility_label') }}</b-label>
      <div class="flex items-center gap-2 bg-gray-800 rounded-lg w-full max-w-xs p-1">
        <button
          type="button"
          @click="isPublic = false"
          class="flex-1 text-center px-4 py-2 text-sm rounded-md transition-colors"
          :class="[!isPublic ? 'bg-white text-black font-semibold' : 'text-gray-400']"
          data-e2e="step3-visibility-private"
        >
          {{ $t('publish_mission.step3.private') }}
        </button>
        <button
          type="button"
          @click="isPublic = true"
          class="flex-1 text-center px-4 py-2 text-sm rounded-md transition-colors"
          :class="[isPublic ? 'bg-white text-black font-semibold' : 'text-gray-400']"
          data-e2e="step3-visibility-public"
        >
          {{ $t('publish_mission.step3.public') }}
        </button>
      </div>
      <p class="text-gray-400 text-sm mt-2">{{ $t('publish_mission.step3.visibility_hint') }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
// TEMPORARILY HIDDEN: import { RadioGroup, RadioGroupOption } from '@headlessui/vue';
import BTextField from '~/components/forms/BTextField.vue';
import BLabel from '~/components/forms/BLabel.vue';
// TEMPORARILY HIDDEN: import BRadioChip from '~/components/forms/radio/BRadioChip.vue';
import BCheckboxGroup from '~/components/forms/checkbox/BCheckboxGroup.vue';
import BCheckboxChip from '~/components/forms/checkbox/BCheckboxChip.vue';
import { SPOKEN_LANGUAGES, DEFAULT_LANGUAGES, type SpokenLanguage } from '~/help/languages';

const { t } = useI18n();

interface Props {
  initialData?: Record<string, any>;
}

const props = defineProps<Props>();

const emit = defineEmits(['change']);

const budget = ref<number | null>(null);
// TEMPORARILY HIDDEN: will be re-enabled later
// const workLocation = ref<string>('remote');
// const durationValue = ref<number | null>(null);
// const durationUnit = ref<'weeks' | 'months'>('weeks');
const isPublic = ref(true);
const languages = ref<SpokenLanguage[]>([...DEFAULT_LANGUAGES]);

const isRestoringData = ref(false);

// TEMPORARILY HIDDEN: will be re-enabled later
// const workLocationOptions = computed(() => [
//   { value: 'remote', label: t('publish_mission.step3.location_remote') },
//   { value: 'hybrid', label: t('publish_mission.step3.location_hybrid') },
//   { value: 'onsite', label: t('publish_mission.step3.location_onsite') },
// ]);

watch(() => props.initialData, (data) => {

  if (data && Object.keys(data).length > 0) {
    isRestoringData.value = true;
    if (data.budget !== undefined) {
      budget.value = data.budget;
    }
    // TEMPORARILY HIDDEN: will be re-enabled later
    // if (data.work_location) {
    //   workLocation.value = data.work_location;
    // }
    // if (data.duration_value !== undefined) {
    //   durationValue.value = data.duration_value;
    // }
    // if (data.duration_unit) {
    //   durationUnit.value = data.duration_unit;
    // }
    if (data.is_public !== undefined) {
      isPublic.value = data.is_public;
    }
    if (Array.isArray(data.languages)) {
      languages.value = data.languages;
    }
    nextTick(() => {
      isRestoringData.value = false;
    });
  }
}, { immediate: true, deep: true });

watch([
  budget,
  // TEMPORARILY HIDDEN: workLocation, durationValue, durationUnit,
  isPublic,
  languages,
], () => {
  if (isRestoringData.value) return;

  emit('change', {
    budget: budget.value,
    // TEMPORARILY HIDDEN: work_location, duration_value, duration_unit
    is_public: isPublic.value,
    languages: languages.value,
  });
}, { deep: true });
</script>
